import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as PrinterIcon } from '../../../assets/SVG/printer.svg';
import useCurrency from '../../CurrencyProvider';
import { formatDateShortMonth, formatDateTime } from '../../../utils/dateUtils';
import { TABLE_HEADER_ROW_SX } from '../../../utils/typographyStyles';
import { usePharmacyPaymentsQuery, PharmacyPaymentTransaction } from '../../../pages/Billing/Pharmacy/pharmacyReceiptHelper';
import { PharmacyReceiptReportGen } from '../../Reports/PharmacyReceipt';
import { PharmacyReceipt } from '../../Reports/PharmacyReceipt/PharmacyReceiptReport';

export interface PharmacyReceiptDialogProps {
  open: boolean;
  onClose: () => void;
  invoiceId: number;
  billNo?: string | null;
  totalBillAmount?: number | string;
}

// Centralized labels and configs 
const LABELS = {
  title: 'Pharmacy Receipt',
  cancelBtn: 'Cancel',
  noReceipts: 'No receipts available for this pharmacy bill.',
  error: 'Failed to load receipts.',
};

const HEADER_CELLS: Array<{
  key: string;
  label: string;
  minWidth?: number;
  align?: 'center' | 'left' | 'right';
}> = [
  { key: 'no', label: 'Payment ID', minWidth: 120 },
  { key: 'amount', label: 'Paid Amount', minWidth: 140, align: 'right' },
  { key: 'date', label: 'Payment Date', minWidth: 140 },
  { key: 'mode', label: 'Payment Mode', minWidth: 120 },
  { key: 'reference', label: 'Reference No.', minWidth: 140 },
  { key: 'status', label: 'Status', minWidth: 100 },
  { key: 'actions', label: 'Actions', align: 'center', minWidth: 80 },
];

const COMMON_CELL_SX = {
  verticalAlign: 'middle' as const,
  px: 2.5,
  py: 1.5,
};

const PharmacyReceiptDialog: React.FC<PharmacyReceiptDialogProps> = ({
  open,
  onClose,
  invoiceId,
  billNo,
  totalBillAmount = 0,
}) => {
  const { formatCurrency } = useCurrency();
  const [printingId, setPrintingId] = useState<number | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [receiptData, setReceiptData] = useState<PharmacyReceipt | null>(null);

  const { data, isLoading, isError } = usePharmacyPaymentsQuery({
    invoiceId,
    enabled: open && invoiceId > 0,
  });

  const transactions = data?.transactions ?? [];
  const hasRows = transactions.length > 0;

  const handlePrintReceipt = (transaction: PharmacyPaymentTransaction) => {
    setPrintingId(transaction.payment_detail_id);
    
    // Calculate total received from non-refunded transactions
    const totalReceived = transactions
      .filter((t) => !t.is_refunded)
      .reduce((sum, t) => sum + (Number(t.paid_amount) || 0), 0);

    // Build receipt data for this specific payment
    const receipt: PharmacyReceipt = {
      receipt_no: `RCP-${transaction.payment_id}`,
      bill_no: billNo || '',
      created_by: '',
      print_date_time: formatDateTime(new Date().toISOString()),
      total_bill_amount: totalBillAmount,
      total_received_amount: totalReceived,
      payment_summary: [transaction], // Only include the selected transaction
    };

    setReceiptData(receipt);
    setReportOpen(true);
    setPrintingId(null);
  };

  const handleReportClose = () => {
    setReportOpen(false);
    setReceiptData(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
          },
        }}
      >
        {/* Title bar */}
        <DialogTitle
          sx={{
            px: 3,
            pt: 2.5,
            pb: 1.5,
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {LABELS.title}
              </Typography>
              {billNo && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  ({billNo})
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="close receipt dialog"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Content: Table */}
        <DialogContent sx={{ px: 0, pt: 0, pb: 0 }}>
          <Box sx={{ px: 3, py: 2 }}>
            {isLoading ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress size={32} />
              </Box>
            ) : isError ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'error.main' }}>
                  {LABELS.error}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                <Table
                  size="small"
                  aria-label="pharmacy receipt table"
                  sx={{
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                    '& th, & td': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  <TableHead>
                    <TableRow sx={TABLE_HEADER_ROW_SX}>
                      {HEADER_CELLS.map((h, index) => (
                        <TableCell
                          key={h.key}
                          align={h.align ?? 'left'}
                          sx={{
                            ...(h.minWidth ? { minWidth: h.minWidth } : {}),
                            whiteSpace: 'nowrap',
                            px: 2.5,
                            ...(index === 0
                              ? {
                                borderTopLeftRadius: 12,
                                borderBottomLeftRadius: 12,
                              }
                              : {}),
                            ...(index === HEADER_CELLS.length - 1
                              ? {
                                borderTopRightRadius: 12,
                                borderBottomRightRadius: 12,
                              }
                              : {}),
                          }}
                        >
                          {h.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hasRows ? (
                      transactions.map((transaction) => (
                        <TableRow
                          key={transaction.payment_detail_id}
                          hover
                          sx={{
                            '&:last-child td': { borderBottom: 0 },
                          }}
                        >
                          <TableCell sx={COMMON_CELL_SX}>
                            <Typography variant="body2">
                              #{transaction.payment_id}
                            </Typography>
                          </TableCell>
                          <TableCell sx={COMMON_CELL_SX} align="right">
                            <Typography variant="body2">
                              {formatCurrency(Number(transaction.paid_amount) || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={COMMON_CELL_SX}>
                            <Typography variant="body2">
                              {formatDateShortMonth(transaction.date)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={COMMON_CELL_SX}>
                            <Typography variant="body2">
                              {transaction.payment_mode_name || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={COMMON_CELL_SX}>
                            <Typography variant="body2">
                              {transaction.reference_number || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={COMMON_CELL_SX}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: transaction.is_refunded ? 'error.main' : 'success.main',
                                fontWeight: 500
                              }}
                            >
                              {transaction.is_refunded ? 'Refunded' : 'Paid'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={COMMON_CELL_SX} align="center">
                            <IconButton
                              size="small"
                              aria-label="print receipt"
                              sx={{ p: 0.5 }}
                              onClick={() => handlePrintReceipt(transaction)}
                              disabled={printingId === transaction.payment_detail_id}
                            >
                              {printingId === transaction.payment_detail_id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <PrinterIcon />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={HEADER_CELLS.length}
                          sx={{ py: 4, textAlign: 'center' }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary' }}
                          >
                            {LABELS.noReceipts}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>

        {/* Footer */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #E5E7EB',
          }}
        >
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={onClose}
              sx={{ px: 4, borderRadius: 999 }}
            >
              {LABELS.cancelBtn}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Pharmacy Receipt Print Report Dialog */}
      <PharmacyReceiptReportGen
        open={reportOpen}
        onClose={handleReportClose}
        data={receiptData}
      />
    </>
  );
};

export default PharmacyReceiptDialog;
